import { useState, useEffect } from "react";
import { FormulaCalculatorMobile } from "./FormulaCalculatorMobile";
import { MathEditorAdapter } from "@/components/math-editor/MathEditorAdapter";

export interface FormulaCalculatorProps {
  value: string;
  onChange: (latex: string) => void;
  onClose?: () => void;
  /** Optional immediate-save hook. When provided, the MathEditor
   *  shows the Save button + Autosave dropdown in its header. */
  onSaveToServer?: (latex: string) => Promise<void>;
}

type DeviceClass = "mobile" | "tablet" | "desktop";

function getDeviceClass(): DeviceClass {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  // Also check touch capability for hybrid devices
  const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  if (w < 640) return "mobile";
  if (w < 1024 || (w < 1280 && hasTouch)) return "tablet";
  return "desktop";
}

export function FormulaCalculator(props: FormulaCalculatorProps) {
  const [device, setDevice] = useState<DeviceClass>(getDeviceClass);

  useEffect(() => {
    function handleResize() { setDevice(getDeviceClass()); }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 2026-05-27: tablet + desktop now route to the new MathEditor
  // (design handoff "MathFormulaEditor"). Mobile keeps the legacy
  // inline calculator because the new editor is explicitly non-mobile
  // (its grids assume tile-wrapping room the phone doesn't have).
  switch (device) {
    case "mobile":
      return <FormulaCalculatorMobile {...props} />;
    case "tablet":
    case "desktop":
      return (
        <MathEditorAdapter
          value={props.value}
          onChange={props.onChange}
          onClose={props.onClose}
          onSaveToServer={props.onSaveToServer}
        />
      );
  }
}
